import { Component } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * Top-level React error boundary.
 * Catches rendering/lifecycle errors that slip past individual components
 * and shows a neutral fallback instead of a blank white screen.
 *
 * Intentionally class-based — React only supports error boundaries as classes.
 */
export class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // In production this is where we'd ship to Sentry / an error aggregator.
    // For now, stderr is enough — dev tools will surface it anyway.
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="min-h-screen flex items-center justify-center p-6 bg-s-2">
        <div className="w-full max-w-md bg-white rounded-xl border border-err-bd p-6">
          <div className="flex items-center gap-2 text-err mb-3">
            <AlertTriangle className="w-5 h-5" />
            <h1 className="text-base font-semibold">Something went wrong</h1>
          </div>
          <p className="text-sm text-t2">
            The panel hit an unexpected error and had to stop rendering.
          </p>
          <pre className="mt-3 text-xs bg-s-2 border border-bd rounded p-2 overflow-auto max-h-40 text-t2">
            {String(this.state.error?.message || this.state.error)}
          </pre>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-4 inline-flex items-center justify-center rounded-lg bg-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-deep"
          >
            Reload page
          </button>
        </div>
      </main>
    );
  }
}
